import { AxiosResponse } from 'axios'
import { axiosInstanceEntity } from './axiosInstance'
import { showToasterMessage } from './entityServices'

/**
 * A saved template: a set of values that pre-fills the create form for one
 * entity type. Called "preset" in code and API, and "Template" in the UI, so it
 * is never confused with `entity_template`, the schema that defines an entity
 * type.
 */
export interface EntityPreset {
    id: string
    name: string
    description: string | null
    entityTemplateId: string
    /** null = the organisation scope. */
    programId: string | null
    programName: string | null
    isDefault: boolean
    quickCreateReady: boolean
    missingRequiredAttributeIds: string[]
    /** {attributeId: value}; entity-typed values come back expanded. */
    attributeValues: Record<string, unknown>
    createdAt: string
    modifiedAt: string | null
}

export interface EntityPresetBody {
    name: string
    description?: string
    entityTemplateId: string
    /** null or omitted (with no virtualOrgId) = the organisation scope. */
    programId?: string | null
    /** A program's virtual org, as the wizard holds it; the server maps it to its program. */
    virtualOrgId?: string
    isDefault: boolean
    attributeValues: Record<string, unknown>
}

export interface FetchEntityPresetsParams {
    /** The logged-in org, whose preset templates these are. */
    orgId: string
    entityTemplateId: string
    /** Omit for every scope; the literal 'null' for the organisation scope. */
    programId?: string
    /** A program's virtual org, as the create wizard holds it. */
    virtualOrgId?: string
    q?: string
}

export type EntityPresetResult<T> =
    | { type: 'success'; payload: T }
    | { type: 'error'; payload: unknown }

/**
 * Lists preset templates for an entity type and all of its sub-types, in one
 * call; each template carries the type it was saved from. `silent` skips the error toast,
 * for the background lookup the create wizard does on open: a failure there
 * must not interrupt creation, which falls back to the legacy defaults.
 */
export const fetchEntityPresets = async (
    params: FetchEntityPresetsParams,
    options: { silent?: boolean } = {},
): Promise<EntityPreset[]> => {
    const { orgId, entityTemplateId, programId, virtualOrgId, q } = params
    return await axiosInstanceEntity
        .get('/entity/preset', {
            params: {
                orgId,
                entityTemplateId,
                programId,
                virtualOrgId,
                q: q || undefined,
            },
        })
        .then((response: AxiosResponse<EntityPreset[]>) => response.data || [])
        .catch((err: unknown) => {
            if (!options.silent) {
                showToasterMessage(err as never, 'error')
            }
            return []
        })
}

export const createEntityPreset = async (
    orgId: string,
    body: EntityPresetBody,
): Promise<EntityPresetResult<EntityPreset>> => {
    return await axiosInstanceEntity
        .post('/entity/preset', body, { params: { orgId } })
        .then(
            (response: AxiosResponse<EntityPreset>) =>
                ({ type: 'success', payload: response.data }) as const,
        )
        .catch((err: unknown) => {
            showToasterMessage(err as never, 'error')
            return { type: 'error', payload: err } as const
        })
}

/** Replaces the whole preset template, so `body.attributeValues` must be complete. */
export const updateEntityPreset = async (
    orgId: string,
    id: string,
    body: EntityPresetBody,
): Promise<EntityPresetResult<EntityPreset>> => {
    return await axiosInstanceEntity
        .put(`/entity/preset/${id}`, body, { params: { orgId } })
        .then(
            (response: AxiosResponse<EntityPreset>) =>
                ({ type: 'success', payload: response.data }) as const,
        )
        .catch((err: unknown) => {
            showToasterMessage(err as never, 'error')
            return { type: 'error', payload: err } as const
        })
}

/** Deletes all the given templates or none: the server fails the whole call if one cannot be deleted. */
export const deleteEntityPresets = async (
    orgId: string,
    ids: string[],
): Promise<EntityPresetResult<null>> => {
    return await axiosInstanceEntity
        .delete('/entity/preset', { params: { orgId }, data: ids })
        .then(() => ({ type: 'success', payload: null }) as const)
        .catch((err: unknown) => {
            showToasterMessage(err as never, 'error')
            return { type: 'error', payload: err } as const
        })
}
